'use client';

import type { TracePreviewBaseDto } from '@terra/shared-contracts-trace';
import { HStack, VStack } from '@terra/ui-styled-system';
import { formatDistanceToNow } from 'date-fns';
import { BotIcon, Clock01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Badge } from '../../components/badge';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../components/typography';
import { ArchivedLifecycleBadge } from '../../components/archived-lifecycle-badge';
import type { MouseEvent } from 'react';
import { toSignalRunItems, type SignalRunItem } from './signal-runs.utils';
import { isTraceAgentName, useTraceAgentLabel } from './use-trace-agent-label';

type SignalRunsTableProps = {
  traces?: TracePreviewBaseDto[];
  onViewTrace?: (trace: TracePreviewBaseDto) => void;
  onTraceContextMenu?: (
    event: MouseEvent<HTMLElement>,
    trace: TracePreviewBaseDto,
  ) => void;
  markArchived?: boolean;
};
const isArchivedTraceRun = (trace: TracePreviewBaseDto): boolean =>
  (trace as { isArchived?: boolean }).isArchived === true;

export const SignalRunsTable = ({
  traces = [],
  onViewTrace,
  onTraceContextMenu,
  markArchived,
}: SignalRunsTableProps) => {
  const t = useTranslations('designSystem.signals.runs');
  const getAgentLabel = useTraceAgentLabel();
  const runs = toSignalRunItems({
    traces,
    executionLabel: t('execution'),
  });
  const handleClick = (run: SignalRunItem) => {
    if (onViewTrace) {
      onViewTrace(run.originalTrace);
    }
  };

  const handTraceContextMenu = (
    event: MouseEvent<HTMLElement>,
    run: SignalRunItem,
  ) => {
    if (onTraceContextMenu) {
      onTraceContextMenu(event, run.originalTrace);
    }
  };

  if (runs.length === 0) {
    return <BodyDefault color="fg.muted">{t('noRuns')}</BodyDefault>;
  }

  return (
    <VStack gap={1} alignItems="stretch">
      {runs.map((run) => (
        <HStack
          key={run.id}
          gap={4}
          justifyContent="space-between"
          py={2}
          px={2}
          mx={-2}
          borderRadius="md"
          cursor="pointer"
          onClick={() => handleClick(run)}
          _hover={{ bg: 'bg.subtle' }}
          onContextMenu={(event) => handTraceContextMenu?.(event, run)}
        >
          <HStack gap={2} flex="1" minW="0">
            <BotIcon size={14} />
            <BodyStrong truncate>{run.name}</BodyStrong>
            {markArchived || isArchivedTraceRun(run.originalTrace) ? (
              <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
            ) : null}
            <Badge size="sm" variant="outline">
              {isTraceAgentName(run.agentName)
                ? getAgentLabel(run.agentName)
                : t('trace')}
            </Badge>
          </HStack>
          <HStack gap={3} flexShrink={0}>
            <Badge size="sm" variant="emphasis">
              {run.status}
            </Badge>
            <HStack gap={1}>
              <Clock01Icon size={12} />
              <MetaDefault color="fg.muted">
                {formatDistanceToNow(run.createdAt, {
                  addSuffix: true,
                })}
              </MetaDefault>
            </HStack>
          </HStack>
        </HStack>
      ))}
    </VStack>
  );
};
